import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlowEditor as SharedFlowEditor, type FlowEditorApi } from '@zengine/flow-editor-ui';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import { listChannels, type ChannelType } from '../api/channels.js';
import {
  listFlows,
  getFlow,
  publishFlow,
  createFlowScaffold,
  deleteFlow,
  saveDraft,
  getDraft,
  discardDraft,
} from '../api/flows.js';

const CHANNEL_LABEL: Record<ChannelType, string> = {
  webchat: 'Webchat',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  five9: 'Five9',
};

const api: FlowEditorApi = {
  getFlow: (channel, tenantSlug) => getFlow(channel, tenantSlug),
  publishFlow: (channel, flow) => publishFlow(channel, flow),
  createScaffold: (channel, tenantSlug, opts) => createFlowScaffold(channel, tenantSlug, opts),
  saveDraft: (channel, draft) => saveDraft(channel, draft),
  getDraft: (channel) => getDraft(channel),
  discardDraft: (channel) => discardDraft(channel),
};

export function Flujos(): JSX.Element {
  const { tenant, slug } = useTenantContext();
  // Backend asimétrico (deuda técnica conocida):
  //  - listFlows (sin auth): escanea S3 por path EXACTO usando query tenantId.
  //    Algunos seeds/migraciones guardan paths bajo slug, otros bajo UUID.
  //  - getFlow (con JWT): resuelve tenant del claim (UUID) y busca por UUID.
  //
  // Pasamos slug a listFlows (compat con seeds) y UUID al editor (lo que
  // realmente usa el handler con auth). Cuando el backend normalice ambos
  // (helper resolveTenant en flow-engine), volvemos a un único id.
  const tenantUuid = tenant?.id ?? '';
  const tenantSlug = slug ?? '';

  const channelsQ = useQuery({
    queryKey: ['channels', tenantUuid],
    queryFn: () => listChannels(tenantUuid),
    enabled: Boolean(tenantUuid),
  });

  const flowsQ = useQuery({
    queryKey: ['flows', tenantSlug, tenantUuid],
    queryFn: async () => {
      const [bySlug, byUuid] = await Promise.allSettled([
        listFlows(tenantSlug),
        listFlows(tenantUuid),
      ]);
      const map = new Map<string, { flowId: string; channel: string; version: string }>();
      for (const r of [bySlug, byUuid]) {
        if (r.status === 'fulfilled') for (const it of r.value) map.set(it.channel, it);
      }
      return Array.from(map.values());
    },
    enabled: Boolean(tenantSlug || tenantUuid),
    retry: false,
  });

  const channels = channelsQ.data ?? [];
  const flows = flowsQ.data ?? [];
  // Únicos channelType del tenant.
  const channelTypes = Array.from(new Set(channels.filter((c) => c.deletedAt === null).map((c) => c.channelType)));
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Flujos"
        subtitle="Diseñá la conversación que tu bot tiene con los usuarios. Visual, JSON o preview en vivo."
      />

      <div className="flex h-[calc(100vh-200px)] gap-4">
        {/* Sidebar canales */}
        <aside className="w-64 shrink-0 panel" style={{ padding: '12px' }}>
          <div className="text-[11px] uppercase tracking-wide text-ink-4 mb-2">Canales</div>
          {channelTypes.length === 0 && (
            <div className="text-xs text-ink-3">
              No hay canales configurados. Andá a Conexiones para crear uno.
            </div>
          )}
          <ul className="space-y-1">
            {channelTypes.map((ch) => {
              const flow = flows.find((f) => f.channel === ch);
              const active = selected === ch;
              return (
                <li key={ch}>
                  <button
                    type="button"
                    onClick={() => setSelected(ch)}
                    className={`w-full text-left rounded px-3 py-2 text-sm transition ${
                      active ? 'bg-brand-500 text-white' : 'hover:bg-bg-2 text-ink-1'
                    }`}
                  >
                    <div className="font-medium">{CHANNEL_LABEL[ch as ChannelType] ?? ch}</div>
                    <div className={`text-[11px] ${active ? 'text-white/80' : 'text-ink-4'}`}>
                      {flow ? `v${flow.version}` : 'Sin publicar'}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Editor */}
        <main className="flex-1 panel flex flex-col" style={{ padding: '12px' }}>
          {!selected && (
            <div className="m-auto text-center text-ink-3 text-sm">
              Seleccioná un canal a la izquierda para editar su flow.
            </div>
          )}
          {selected && tenantUuid && (
            <>
              <SharedFlowEditor flowId={selected} tenantSlug={tenantUuid} api={api} />
              <DangerZone channel={selected} tenantId={tenantUuid} onDeleted={() => setSelected(null)} />
            </>
          )}
        </main>
      </div>
    </>
  );
}

function DangerZone({
  channel,
  tenantId,
  onDeleted,
}: {
  channel: string;
  tenantId: string;
  onDeleted: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => deleteFlow(channel, tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      qc.invalidateQueries({ queryKey: ['flow', tenantId, channel] });
      onDeleted();
    },
  });
  return (
    <div className="mt-3 flex items-center justify-end gap-2 border-t border-bg-2 pt-2">
      {del.error && <span className="text-[11px] text-err">{(del.error as Error).message}</span>}
      <button
        type="button"
        className="rounded border border-err/40 bg-err/10 px-2 py-1 text-[11px] text-err hover:bg-err/20 disabled:opacity-50"
        disabled={del.isPending}
        onClick={() => {
          if (window.confirm(`Eliminar flow del canal "${channel}"? Esto no se puede deshacer.`)) {
            del.mutate();
          }
        }}
      >
        {del.isPending ? 'Eliminando…' : '🗑 Eliminar flow'}
      </button>
    </div>
  );
}
